/**
 * Tiny dot-path resolver used by every `api-*` widget to extract
 * fields from arbitrary JSON responses.
 *
 * Supported syntax:
 *   "data.items"      → obj.data.items
 *   "data.items.0"    → obj.data.items[0]
 *   "users.0.name"    → obj.users[0].name
 *   ""  (or undefined)→ returns the whole object
 *
 * Deliberately NOT supported (yet):
 *   - Bracket notation:    `users[0]` / `metadata['x.y']`
 *   - Wildcards:           `items.*.name`
 *   - Filters:             `items[?(@.active)]`
 *
 * Why so minimal: ~95 % of real-world paths fit `a.b.c.0.d`, and
 * limiting the grammar means a malformed path produces `undefined`
 * cleanly instead of throwing — important for a widget that an
 * admin is editing live in the scaffolder.
 *
 * If you need a key that legitimately contains a dot (Kubernetes
 * annotations like `bosch.io/owner` are the classic case), pre-flatten
 * the response in the backend proxy or fetch via a transform layer —
 * we don't want to teach this util JSONPath because the bracket
 * grammar makes mistakes silent for non-power-users.
 */
export const getJsonPath = (
  source: unknown,
  path: string | undefined,
): unknown => {
  if (!path) return source;
  const parts = path.split('.').filter(p => p.length > 0);
  let current: unknown = source;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    // Array index lookups work transparently because JS coerces the
    // string index to a number when indexing arrays — no special
    // case needed.
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

/**
 * Coerce an unknown value to a primitive renderable string.
 *
 * Why we don't just `String(x)`:
 *   - `String(undefined)` is the literal string "undefined", which
 *     looks like a real value in a stat card.
 *   - `String({a: 1})` is "[object Object]", which is worse.
 *
 * For unknown shapes we fall back to JSON to give the admin a
 * readable hint about why the value didn't fit (e.g. "extracted
 * `{x: 1}` for a stat card — path probably needs deeper drill-in").
 */
export const toDisplayPrimitive = (
  value: unknown,
  fallback = '—',
): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') {
    // Format large numbers with thousands separators — a raw
    // `1247392` is harder to read than `1,247,392` in a KPI tile.
    // Locale 'en-US' is hard-coded so the widget renders the same
    // way for everyone on the team regardless of OS locale.
    return value.toLocaleString('en-US');
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
};

/**
 * Coerce an unknown value to a number, returning `undefined` for
 * anything that doesn't fit (instead of NaN, which is a common
 * footgun for chart libraries that then render an empty plot).
 */
export const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};
